import { useMemo, useState } from 'react';
import { evalImportPositions } from '../../services/cmm-import/evalImportPositions.ts';
import { CmmPositionRead } from '../../services/cmm-import/types.ts';
import { ProtocolFinal, RecordDraft } from '../../types/sharedTypeImpl.ts';
import { ModalSubmitDialog } from '../modalSubmitDialog.tsx';


interface PartSummary {
    filePartNumber: number; // 1-based for display
    totalPositions: number;
    failedCount: number;
    importableCount: number;
    isFullyFailed: boolean;
    isPartiallyFailed: boolean;
}

interface Props {
    isOpen: boolean;
    filename: string;
    protocol: ProtocolFinal;
    partGroups: CmmPositionRead[][];
    record: RecordDraft;
    currentPartIndex: number;
    onCancel: () => void;
    /** startPartIndex is 0-based; expandToFit means add blank parts to cover all groups */
    onSubmit: (startPartIndex: number, expandToFit: boolean, includePartiallyFailed: boolean) => void;
}

export function MultiPartImportDialog({
    isOpen,
    filename,
    protocol,
    partGroups,
    record,
    currentPartIndex,
    onCancel,
    onSubmit,
}: Props) {
    const partsInFile = partGroups.length;
    const currentPartCount = record.parts.length;
    const evalPartIndex = Math.min(currentPartIndex, Math.max(0, currentPartCount - 1));

    const [startPartIndex, setStartPartIndex] = useState(currentPartIndex);
    const [includePartiallyFailed, setIncludePartiallyFailed] = useState(false);

    const partSummaries: PartSummary[] = partGroups.map((group, i) => {
        const evaluated = evalImportPositions(protocol, record, evalPartIndex, group);
        const importableCount = evaluated.filter((p) => p.isImportable).length;
        const failedCount = group.filter(p => p.issue).length;
        const isFullyFailed = importableCount === 0;
        return {
            filePartNumber: i + 1,
            totalPositions: group.length,
            failedCount,
            importableCount,
            isFullyFailed,
            isPartiallyFailed: !isFullyFailed && failedCount > 0,
        };
    });

    const totalFailed = partSummaries.reduce((sum, p) => sum + p.failedCount, 0);
    const importablePartsCount = partSummaries.filter((p) => !p.isFullyFailed && (!p.isPartiallyFailed || includePartiallyFailed)).length;
    const excludedFullyFailedParts = partSummaries.filter((p) => p.isFullyFailed).length;
    const partiallyFailedCount = partSummaries.filter((p) => p.isPartiallyFailed).length;

    const neededCount = startPartIndex + importablePartsCount;
    const hasDiscrepancy = neededCount > currentPartCount;
    const [expandToFit, setExpandToFit] = useState(neededCount > currentPartCount);

    const endIndex = startPartIndex + importablePartsCount;

    // Parts inside the record that already have at least one filled value and would be overwritten
    const existingFilledPartNumbers = useMemo(() => {
        return record.parts
            .slice(startPartIndex, Math.min(endIndex, currentPartCount))
            .map((part, i) => ({
                displayNumber: startPartIndex + i + 1,
                hasFilled: part.positionValues.some(pv => pv.value != null && pv.value !== ''),
            }))
            .filter(p => p.hasFilled)
            .map(p => p.displayNumber);
    }, [record.parts, startPartIndex, endIndex, currentPartCount]);

    const handleStartIndexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseInt(e.target.value, 10);
        if (!isNaN(val) && val >= 1 && val <= currentPartCount) {
            setStartPartIndex(val - 1);
        }
    };

    if (!isOpen) return null;
    return (
        <ModalSubmitDialog
            isOpen={isOpen}
            onCancel={onCancel}
            onSubmit={() => onSubmit(startPartIndex, expandToFit, includePartiallyFailed)}
            submitButtonText='Import'
        >
            <h3 style={{ marginTop: 0 }}>Multi-part import: {filename}</h3>

            <table style={{ borderCollapse: 'collapse', marginBottom: '12px' }}>
                <tbody>
                    <tr>
                        <td style={{ paddingRight: '16px' }}>Parts found in file:</td>
                        <td>
                            <strong>{partsInFile}</strong>
                        </td>
                    </tr>
                    <tr>
                        <td>Importable parts (used):</td>
                        <td>
                            <strong>{importablePartsCount}</strong> / {partsInFile}
                        </td>
                    </tr>
                    {partiallyFailedCount > 0 && (
                        <tr>
                            <td>Partially failed parts:</td>
                            <td>
                                <strong className='warning'>{partiallyFailedCount}</strong>
                            </td>
                        </tr>
                    )}
                    {excludedFullyFailedParts > 0 && (
                        <tr>
                            <td>Fully failed parts (excluded):</td>
                            <td>
                                <strong className='warning'>{excludedFullyFailedParts}</strong>
                            </td>
                        </tr>
                    )}
                    {totalFailed > 0 && (
                        <tr>
                            <td>Total failed measurements:</td>
                            <td>
                                <strong className='error'>{totalFailed}</strong>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {partiallyFailedCount > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginBottom: '12px' }}>
                    <input type='checkbox' checked={includePartiallyFailed} onChange={(e) => setIncludePartiallyFailed(e.target.checked)} />
                    Include partially failed parts
                </label>
            )}

            {hasDiscrepancy && (
                <div className='warning' style={{ padding: '8px 12px', marginBottom: '12px', border: '1px solid orange', borderRadius: '4px' }}>
                    <p style={{ margin: '0 0 6px 0' }}>
                        Import requires <strong>{neededCount}</strong> parts from selected start index, but the record currently has{' '}
                        <strong>{currentPartCount}</strong>.
                    </p>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type='checkbox' checked={expandToFit} onChange={(e) => setExpandToFit(e.target.checked)} />
                        Expand record to {neededCount} parts to import all importable parts
                    </label>
                </div>
            )}

            <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Start importing from part:
                    <input
                        type='number'
                        min={1}
                        max={currentPartCount}
                        value={startPartIndex + 1}
                        onChange={handleStartIndexChange}
                        style={{ width: '64px' }}
                    />
                    <span style={{ color: '#666' }}>of {currentPartCount}</span>
                </label>
            </div>

            {existingFilledPartNumbers.length > 0 && (
                <p className='warning' style={{ margin: '0 0 12px 0' }}>
                    Part{existingFilledPartNumbers.length !== 1 ? 's' : ''} {existingFilledPartNumbers.join(', ')} already have measurements and will
                    be overwritten.
                </p>
            )}

            <div className='dialog--button-bar' style={{ marginBottom: '16px' }}>
                <button onClick={onCancel}>Cancel</button>
                <button onClick={() => onSubmit(startPartIndex, expandToFit, includePartiallyFailed)}>Import</button>
            </div>

            <table className='position-import-table' style={{ marginTop: '8px' }}>
                <thead>
                    <tr>
                        <th>Part (file)</th>
                        <th>Positions</th>
                        <th>Failed</th>
                        <th>Importable</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {partSummaries.map((s) => (
                        <tr key={s.filePartNumber}>
                            <td>{s.filePartNumber}</td>
                            <td>{s.totalPositions}</td>
                            <td>{s.failedCount > 0 ? <span className='error'>{s.failedCount}</span> : 0}</td>
                            <td>{s.importableCount}</td>
                            <td
                                style={{
                                    color: s.isFullyFailed ? 'darkorange' : s.isPartiallyFailed && !includePartiallyFailed ? 'darkorange' : 'green',
                                }}
                            >
                                {s.isFullyFailed
                                    ? 'Fully failed (excluded)'
                                    : s.isPartiallyFailed && !includePartiallyFailed
                                      ? 'Partially failed (excluded)'
                                      : '✓ Included'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </ModalSubmitDialog>
    );
}
